/**
 * Turn a file-input change or a drag-and-drop into an upload-ready list, with
 * the same type gate and folder handling as the Data Sources picker
 * (`FileSelection`). Used wherever files are added to an existing deal
 * (DOP-608): the review dialog's drop zone and a drop onto a sidebar deal.
 */

import { validateFileType } from "../components/FileSelection";
import {
	collectDroppedEntries,
	collectFolderFiles,
	relativePathFor,
} from "./folderUpload";

export interface PickedFiles {
	files: File[];
	/** Plain-language reasons some of what was picked was left out. */
	problems: string[];
}

const plural = (count: number, noun: string) =>
	`${count} ${noun}${count === 1 ? "" : "s"}`;

export const pickedFromList = (list: FileList | File[] | null): PickedFiles => {
	const all = Array.from(list ?? []);
	const files = all.filter(validateFileType);
	const rejected = all.length - files.length;
	return {
		files,
		problems:
			rejected > 0
				? [`${plural(rejected, "file")} left out: that file type isn't supported.`]
				: [],
	};
};

/**
 * Read a drop. Must be called synchronously from the drop handler: the
 * DataTransfer is neutered once the handler returns, so the file list and the
 * entries are both captured before the first await.
 */
export const pickedFromDrop = async (
	dataTransfer: DataTransfer
): Promise<PickedFiles> => {
	const flatFiles = Array.from(dataTransfer.files);
	let traversal;
	try {
		traversal = await collectDroppedEntries(dataTransfer);
	} catch {
		return {
			files: [],
			problems: ["That folder couldn't be read. Try choosing the files instead."],
		};
	}
	if (traversal === null) return pickedFromList(flatFiles);

	const paths = new Map(
		traversal.entries.map(({ file, fullPath }) => [file, fullPath])
	);
	const collected = collectFolderFiles(
		traversal.entries.map(({ file }) => file),
		validateFileType,
		(file) => paths.get(file) ?? file.name,
		traversal.failed
	);
	const blocked = new Set([
		...collected.unstorable.map((entry) => entry.relativePath),
		...collected.collisions.map((entry) => entry.relativePath),
	]);
	const problems: string[] = [];
	if (collected.skippedUnsupported > 0) {
		problems.push(
			`${plural(collected.skippedUnsupported, "file")} left out: that file type isn't supported.`
		);
	}
	if (collected.failed.length > 0) {
		problems.push(`${plural(collected.failed.length, "file")} couldn't be read.`);
	}
	if (blocked.size > 0) {
		problems.push(
			`${plural(blocked.size, "file")} left out: the name is too long or used twice.`
		);
	}
	// `collectFolderFiles` registered each file's stored path, which is what
	// `blocked` holds.
	return {
		files: collected.files.filter((file) => !blocked.has(relativePathFor(file))),
		problems,
	};
};
